import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, Link } from 'react-router-dom';

const legalNavItems = [
  { path: '/terms', label: 'Terms of Service' },
  { path: '/privacy', label: 'Privacy Policy' },
  { path: '/cookies', label: 'Cookie Policy' },
  { path: '/aml', label: 'AML Policy' },
  { path: '/risk', label: 'Risk Disclosure' },
  { path: '/agreement', label: 'User Agreement' },
];

const languageOptions = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh-TW', label: '繁體中文', short: '繁中' },
  { code: 'ja', label: '日本語', short: 'JP' },
  { code: 'ko', label: '한국어', short: 'KR' },
  { code: 'th', label: 'ภาษาไทย', short: 'TH' },
];

const pathToNavKey: Record<string, string> = {
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/cookies': 'cookies',
  '/aml': 'aml',
  '/risk': 'risk',
  '/agreement': 'agreement',
};

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

export default function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps) {
  const { isDark, toggleTheme } = useTheme();
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    if (!langOpen) return;
    const handleClick = () => setLangOpen(false);
    setTimeout(() => document.addEventListener('click', handleClick, { once: true }), 0);
    return () => document.removeEventListener('click', handleClick);
  }, [langOpen]);

  const currentLang = languageOptions.find((l) => l.code === i18n.language) || languageOptions[0];

  const bgMain = isDark ? 'bg-[#050505]' : 'bg-[#f5f5f5]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';

  return (
    <div className={`relative min-h-screen ${bgMain} ${textPrimary}`}>
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className={`absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full blur-[150px] ${isDark ? 'bg-[#00ff88]/[0.02]' : 'bg-[#00ff88]/[0.04]'}`} />
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${borderLight}`}>
        <div className="w-full px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 border border-[#00ff88]/20">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20futuristic%20AI-themed%20crypto%20exchange%20logo%20featuring%20the%20letters%20A%20and%20I%20fused%20into%20a%20single%20elegant%20monogram%20with%20electric%20neon%20green%20gradient%20glow%20and%20neural%20network%20node%20connections%2C%20minimal%20geometric%20design%20on%20a%20deep%20black%20background%20with%20subtle%20digital%20grid%20texture%2C%20professional%20brand%20identity%2C%20high%20resolution%2C%20clean%20sharp%20edges%2C%20no%20additional%20text%20or%20elements%2C%20centered%20composition%2C%20perfect%20for%20dark%20themed%20trading%20platform%20website%20header%20and%20app%20icon&width=256&height=256&seq=logo_zhisu_ai_v1&orientation=squarish"
                  alt="ZhiSu"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className={`font-semibold text-sm tracking-tight ${textPrimary}`}>ZhiSu</span>
            </Link>
            <span className={textMuted}>/</span>
            <span className={`text-sm ${textSecondary}`}>{t('layout.legal', 'Legal')}</span>
            <span className={textMuted}>/</span>
            <span className="text-sm font-medium">{title}</span>
          </div>

          {/* Language + Theme toggles */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLangOpen(!langOpen);
                }}
                className={`hidden sm:flex h-9 items-center gap-1.5 px-3 rounded-lg border text-xs font-medium transition-all whitespace-nowrap ${
                  isDark
                    ? 'border-white/10 text-[#999] hover:text-white hover:bg-white/5 hover:border-white/20'
                    : 'border-black/10 text-[#666] hover:text-[#111] hover:bg-black/5 hover:border-black/20'
                }`}
                aria-label="Select language"
              >
                <span>{currentLang.short}</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute right-0 top-full mt-2 w-40 rounded-xl border overflow-hidden shadow-lg backdrop-blur-xl z-50 ${
                      isDark
                        ? 'bg-[#0a0a0a]/95 border-white/[0.08]'
                        : 'bg-white/95 border-black/[0.08]'
                    }`}
                  >
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setLangOpen(false);
                        }}
                        className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors text-left ${
                          i18n.language === lang.code
                            ? isDark
                              ? 'text-[#00ff88] bg-white/5'
                              : 'text-[#00ff88] bg-black/5'
                            : isDark
                              ? 'text-[#999] hover:text-white hover:bg-white/5'
                              : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                        }`}
                      >
                        <span className="font-medium">{lang.label}</span>
                        {i18n.language === lang.code && (
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center ml-auto text-[#00ff88]" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg border transition-all ${
                isDark
                  ? 'border-white/10 text-[#999] hover:text-white hover:bg-white/5 hover:border-white/20'
                  : 'border-black/10 text-[#666] hover:text-[#111] hover:bg-black/5 hover:border-black/20'
              }`}
              aria-label="Toggle theme"
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} w-4 h-4 flex items-center justify-center text-lg`} />
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className={`sticky top-8 p-4 rounded-xl border ${borderLight} ${bgCard}`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${textMuted}`}>
                {t('layout.legalDocuments', 'Legal Documents')}
              </h3>
              <ul className="space-y-1">
                {legalNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const navKey = pathToNavKey[item.path];
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        className={`block px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'bg-[#00ff88]/10 text-[#00ff88] font-medium'
                            : `${textSecondary} hover:${textPrimary} hover:bg-white/5`
                        }`}
                      >
                        {navKey ? t(`nav.${navKey}`, item.label) : item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard}`}>
              <div className="mb-8 pb-6 border-b border-dashed border-[#00ff88]/20">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">{title}</h1>
                <p className={`text-sm ${textMuted}`}>
                  {t('common.lastUpdated', 'Last Updated')}: <span className="font-mono">{lastUpdated}</span>
                </p>
              </div>
              <div className="prose prose-sm max-w-none legal-content">
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className={`relative z-10 border-t ${borderLight} mt-12`}>
        <div className="w-full px-4 md:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className={`text-xs ${textMuted}`}>
            &copy; 2026 ZhiSu. {t('footer.allRights', 'All rights reserved.')}
          </p>
          <Link to="/" className={`text-xs ${textSecondary} hover:text-[#00ff88] transition-colors`}>
            {t('layout.backToHome', 'Back to Home')}
          </Link>
        </div>
      </footer>
    </div>
  );
}